import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import ContactInfo from "../../../_components/steps/ContactInfo";
import VehicleInfo from "../../../_components/steps/VehicleInfos";
import EuropeAddressInfo from "../../../_components/steps/EuropeAddressInfo";
import EuropeScheduleInfo from "../../../_components/steps/EuropeScheduleInfo";
import { useToaster } from "../../../_components/toaster/MuiToasterProvider";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import {
  clientUpdate,
  enrolleeUpdate,
  registerEnrollee,
  saveEnrollee,
  loadEnrollee,
} from "../../../_redux/actions/enrolleeActions";
import {
  loadVehicle,
  saveNewVehicle,
} from "../../../_redux/actions/vehicleActions";
import { LifeSafer } from "../../../_helpers/BrandConstants";
import { langConversionHelper, utilities } from "@/_helpers";
import Background from "../../../_images/Background.png";
import BackgroundMobile from "../../../_images/Background-mobile.png";
import ContinueButton from "../../../_components/steps/ContinueButton";

const STORY_NAME = "Europe";
const VEHICLE_STEP_INDEX = 2;

const resolveVehicleId = (vehicle = {}) =>
  vehicle?.vehicleId ??
  vehicle?.VehicleId ??
  vehicle?.vehicleID ??
  vehicle?.VehicleID ??
  null;

const normalizeEuropeStep = (step) => {
  const normalizedStep = String(step || "")
    .trim()
    .toLowerCase();
  return (
    {
      welcome: "Welcome",
      contact: "Contact",
      vehicle: "Vehicle",
      address: "Address",
      schedule: "Schedule",
    }[normalizedStep] || "Welcome"
  );
};

const normalizeEuropeSession = (session = {}, currentStep) => {
  const normalized = utilities.normalizeEnrolleeSessionData({ ...session });
  const step = normalizeEuropeStep(
    currentStep ?? normalized.Step ?? normalized.step,
  );

  normalized.step = step;
  normalized.story = STORY_NAME;
  normalized.enrolleeId =
    normalized.enrolleeId ?? normalized.EnrolleeId ?? normalized.EnrolleeID;
  normalized.userId = normalized.userId ?? normalized.UserId;
  normalized.clientId = normalized.clientId ?? normalized.ClientId;
  normalized.firstName = normalized.firstName ?? normalized.FirstName;
  normalized.lastName = normalized.lastName ?? normalized.LastName;
  normalized.cellPhone = normalized.cellPhone ?? normalized.CellPhone;
  normalized.homePhone = normalized.homePhone ?? normalized.HomePhone;
  normalized.email = normalized.email ?? normalized.Email;
  normalized.homeAddr1 =
    normalized.homeAddr1 ??
    normalized.HomeAddr1 ??
    normalized.homeAddress1 ??
    normalized.HomeAddress1 ??
    normalized.address1;
  normalized.homeAddr2 =
    normalized.homeAddr2 ??
    normalized.HomeAddr2 ??
    normalized.homeAddress2 ??
    normalized.HomeAddress2 ??
    normalized.address2;
  normalized.city = normalized.city ?? normalized.City;
  normalized.state =
    normalized.state ??
    normalized.State ??
    normalized.province ??
    normalized.Province;
  normalized.province = normalized.province ?? normalized.state;
  normalized.zip =
    normalized.zip ??
    normalized.Zip ??
    normalized.postalCode ??
    normalized.PostalCode;
  normalized.country = normalized.country ?? normalized.Country;
  normalized.countryCode =
    normalized.countryCode ?? normalized.CountryCode ?? normalized.country;
  normalized.vehicleId = resolveVehicleId(normalized);

  [
    "Step",
    "Story",
    "EnrolleeId",
    "EnrolleeID",
    "UserId",
    "ClientId",
    "FirstName",
    "LastName",
    "CellPhone",
    "HomePhone",
    "Email",
    "HomeAddr1",
    "homeAddress1",
    "HomeAddress1",
    "address1",
    "HomeAddr2",
    "homeAddress2",
    "HomeAddress2",
    "address2",
    "City",
    "State",
    "Province",
    "Zip",
    "postalCode",
    "PostalCode",
    "Country",
    "CountryCode",
    "VehicleId",
    "VehicleID",
    "vehicleID",
    "selectedVehicleID",
  ].forEach((key) => delete normalized[key]);

  return normalized;
};

const getProtectionPlanValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value ? "True" : "False";
  return String(value);
};

const buildClientUpdatePayload = (data = {}, ids = {}) => ({
  clientId: ids.clientId ?? data.clientId ?? data.ClientId ?? 0,
  userId: ids.userId ?? data.userId ?? data.UserId ?? 0,
  firstName: data.firstName ?? data.FirstName ?? null,
  lastName: data.lastName ?? data.LastName ?? null,
  dob: data.dob ?? data.DOB ?? null,
  homeAddr1:
    data.homeAddr1 ??
    data.HomeAddr1 ??
    data.homeAddress1 ??
    data.address1 ??
    null,
  homeAddr2:
    data.homeAddr2 ??
    data.HomeAddr2 ??
    data.homeAddress2 ??
    data.address2 ??
    null,
  city: data.city ?? data.City ?? null,
  zip: data.zip ?? data.Zip ?? data.postalCode ?? data.PostalCode ?? null,
  state: data.state ?? data.State ?? data.province ?? data.Province ?? null,
  county: data.county ?? data.County ?? null,
  country: data.country ?? data.Country ?? data.countryCode ?? null,
  homeLat: data.homeLat ?? data.HomeLat ?? data.homeLatitude ?? 0,
  homeLong: data.homeLong ?? data.HomeLong ?? data.homeLongitude ?? 0,
  dlState: data.dlState ?? data.DlState ?? null,
  dlNum: data.dlNum ?? data.DlNum ?? "CE - NEED",
  dlExpirationDate: data.dlExpirationDate ?? data.DlExpirationDate ?? null,
  email: data.email ?? data.Email ?? null,
  homePhone: data.homePhone ?? data.HomePhone ?? null,
  cellPhone: data.cellPhone ?? data.CellPhone ?? null,
  workPhone: data.workPhone ?? data.WorkPhone ?? null,
  programType: data.programType ?? data.ProgramType ?? null,
  protPlan: getProtectionPlanValue(data.protPlan ?? data.ProtPlan),
  contractEnd: data.contractEnd ?? data.ContractEnd ?? 0,
  portalID: data.portalID ?? data.PortalID ?? 3,
  preferredContactId: data.preferredContactId ?? data.PreferredContactId ?? 0,
  convictionStateId:
    data.convictionStateId ??
    data.ConvictionStateId ??
    data.ConvictionStateID ??
    0,
  convictionCountyId:
    data.convictionCountyId ??
    data.ConvictionCountyId ??
    data.ConvictionCountyID ??
    0,
  hearAboutID: data.hearAboutID ?? data.HearAboutID ?? 0,
  hearAboutName: data.hearAboutName ?? data.HearAboutName ?? null,
  deviceType: data.deviceType ?? data.DeviceType ?? null,
  convictionCountryId:data?.convictionCountryId,
});

const buildClientSavePayload = (data, ids) => ({
  ...buildClientUpdatePayload(data, ids),
  enrolleeId: ids.enrolleeId,
  languageID: data.languageID ?? 0,
});

const EuropeStory = ({
  activeStep,
  setActiveStep,
  hasSetInitialStep,
  isLoaded,
  registerEnrollee,
  enrolleeUpdate,
  saveEnrollee,
  clientUpdate,
  saveNewVehicle,
  loadVehicle,
  loadEnrollee,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToaster();
  const cookies = useRef(new Cookies()).current;
  const enrolleeUser = JSON.parse(sessionStorage.getItem("enrolleeUser")) || {};
  const enrollNow = cookies.get("EnrollNow") || {};
  const enrollmentCode = cookies.get("lsEnrollmentCode") || {};
  const brand = cookies.get("lsEnrollmentReferral") || LifeSafer;
  const [submitting, setSubmitting] = useState(false);
  const [pereferedContact, setPreferedContact] = useState(
    enrolleeUser?.contactPreference || "Phone",
  );
  const jwtTokenData = cookies.get("lifesaferEnrollment");
  const [isEnrolleeLoadNeeded, setIsEnrolleeLoadNeeded] = useState(false);
  const vehicleResumeHandledRef = useRef(false);
  const EnrollCookieData = cookies.get("EnrollNow") || {};
  let enrolleeDataArray = useRef([]);
  const enrollmentCodeData = cookies.get("lsEnrollmentCode");
  const [enrolleeData,setEnrolleeData]=useState([])

  const {
    UTM_source,
    UTM_medium,
    UTM_campaign,
    GA_clientid,
    gclid,
    UserAgent,
    IPAddress,
    wbraid,
    gbraid,
    fbclid,
    fbp,
    ttclid,
    rdt_cid,
    Page: rawPage,
    URL,
  } = EnrollCookieData;

  const Page = rawPage?.substring(0, 150) || "";

  useEffect(() => {
    const previousLanguage = i18n.language;
    const previousLangPreference = localStorage.getItem("lang");
    const supportedLanguages = langConversionHelper.languageList();
    const cookieLanguage =
      enrollNow.LanguageId ??
      enrollNow.languageId ??
      enrollNow.LanguageID ??
      enrollNow.languageID ??
      enrollNow.Language ??
      enrollNow.language;
    const enrollmentCodeLanguage =
      enrollmentCodeData?.languageId ??
      enrollmentCodeData?.LanguageId ??
      enrollmentCodeData?.languageID ??
      enrollmentCodeData?.LanguageID;
    const languageToUse = [cookieLanguage, enrollmentCodeLanguage]
      .map((preferredLanguage) => {
        const normalizedPreferredLanguage = String(
          preferredLanguage ?? "",
        ).toLowerCase();
        const matchedLanguage = supportedLanguages.find(
          (language) =>
            language.langAbbrev.toLowerCase() ===
              normalizedPreferredLanguage ||
            language.nativeName.toLowerCase() ===
              normalizedPreferredLanguage ||
            language.displayName.toLowerCase() ===
              normalizedPreferredLanguage ||
            String(language.languageId ?? language.languageID) ===
              normalizedPreferredLanguage,
        );
        const preferredLanguageCode =
          (matchedLanguage?.languageId ?? matchedLanguage?.languageID) === 1
            ? "enUS"
            : matchedLanguage?.langAbbrev;
        return preferredLanguageCode &&
          i18n.hasResourceBundle(preferredLanguageCode, "translation")
          ? preferredLanguageCode
          : null;
      })
      .find(Boolean) || "enUS";

    i18n.changeLanguage(languageToUse);
    localStorage.setItem("lang", languageToUse);

    return () => {
      i18n.changeLanguage(previousLanguage || "enUS");
      if (previousLangPreference === null) {
        localStorage.removeItem("lang");
      } else {
        localStorage.setItem("lang", previousLangPreference);
      }
    };
  }, []);

  useEffect(() => {
    const currentSessionEnrollee =
      JSON.parse(sessionStorage.getItem("enrolleeUser")) || {};

    const currentClientId =
      currentSessionEnrollee?.clientId || currentSessionEnrollee?.ClientId;

    const currentUserId =
      currentSessionEnrollee?.userId || currentSessionEnrollee?.UserId;
    async function fetchEnrollee() {
      if (currentClientId && currentUserId) {
        const result = await loadEnrollee(currentClientId, currentUserId);
        setEnrolleeData(result);
      }
    }
    
    fetchEnrollee();
  }, [activeStep]);


  const contactInfoForm = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: enrolleeUser.firstName || enrolleeUser.FirstName || null,
      lastName: enrolleeUser.lastName || enrolleeUser.LastName || null,
      cellPhone: enrolleeUser.cellPhone || enrolleeUser.CellPhone || null,
      email: enrolleeUser.email || enrolleeUser.Email || null,
      preferedContact: null,
      call_me_out: null,
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeStep]);

  useEffect(() => {
    const storedSession =
      JSON.parse(sessionStorage.getItem("enrolleeUser")) || {};
    sessionStorage.setItem(
      "enrolleeUser",
      JSON.stringify(normalizeEuropeSession(storedSession)),
    );
  }, []);

  const persistSession = (currentStep, updateData = {}, ids = {}) => {
    const current =
      JSON.parse(sessionStorage.getItem("enrolleeUser")) || enrolleeUser;
    const next = normalizeEuropeSession(
      {
        ...current,
        ...updateData,
        ...ids,
      },
      currentStep,
    );
    sessionStorage.setItem("enrolleeUser", JSON.stringify(next));
    return next;
  };

  const resolveIds = () => {
    const current =
      JSON.parse(sessionStorage.getItem("enrolleeUser")) || enrolleeUser;
    const authenticatedEnrollee = cookies.get("lifesaferEnrollment") || {};
    return {
      enrolleeId:
        current.enrolleeId ||
        current.EnrolleeId ||
        authenticatedEnrollee.enrolleeId ||
        authenticatedEnrollee.EnrolleeId,
      userId:
        current.userId ||
        current.UserId ||
        authenticatedEnrollee.userId ||
        authenticatedEnrollee.UserId,
      clientId:
        current.clientId ||
        current.ClientId ||
        authenticatedEnrollee.clientId ||
        authenticatedEnrollee.ClientId,
    };
  };

  const createClientUpdatePayload = (updateData = {}, ids = resolveIds()) => {
    const currentSession =
      JSON.parse(sessionStorage.getItem("enrolleeUser")) || enrolleeUser;
    return buildClientUpdatePayload(
      {
        ...currentSession,
        ...updateData,
      },
      ids,
    );
  };

  const persistVehicleSelection = (vehicle, step = "Vehicle") => {
    const vehicleId = resolveVehicleId(vehicle);
    if (!vehicleId) return null;

    const vehicleWithoutUserId = { ...vehicle };
    delete vehicleWithoutUserId.userId;

    persistSession(step, {
      ...vehicleWithoutUserId,
      vehicleId,
      VehicleId: vehicleId,
      VehicleID: vehicleId,
      selectedVehicleID: vehicleId,
      europeVehicleSelectionConfirmed: true,
    });

    return vehicleId;
  };

  const trackingData = {
    utM_source: enrollNow.UTM_source || null,
    utM_medium: enrollNow.UTM_medium || null,
    utM_campaign: enrollNow.UTM_campaign || null,
    gA_clientid: enrollNow.GA_clientid || null,
    gclID: enrollNow.gclid || null,
    userAgent: enrollNow.UserAgent || null,
    ipAddress: enrollNow.IPAddress || null,
    wbraid: enrollNow.wbraid || null,
    gbraid: enrollNow.gbraid || null,
    fbclid: enrollNow.fbclid || null,
    fbp: enrollNow.fbp || null,
    ttclid: enrollNow.ttclid || null,
    rdt_cid: enrollNow.rdt_cid || null,
    page: enrollNow.Page?.substring(0, 150) || null,
    referURL: enrollNow.URL?.slice(0, 500) || null,
  };

  const saveContact = async (updateData) => {
    let ids = resolveIds();
    const jwt = cookies.get("lifesaferEnrollment")?.jwtToken;
    const contactData = {
      ...updateData,
      firstName: updateData.firstName ?? updateData.FirstName ?? null,
      lastName: updateData.lastName ?? updateData.LastName ?? null,
      cellPhone: updateData.cellPhone ?? updateData.CellPhone ?? null,
      homePhone:
        updateData.homePhone ??
        updateData.HomePhone ??
        updateData.cellPhone ??
        updateData.CellPhone ??
        null,
      email: updateData.email ?? updateData.Email ?? null,
      ...trackingData,
      Story: STORY_NAME,
      story: STORY_NAME,
      Step: "Contact",
      BrandIdentifier: brand,
      contactPreference: pereferedContact || "Phone",
      EnrollmentCodeId:
        updateData.EnrollmentCodeId ||
        enrollmentCode.enrollmentCodeId ||
        enrollmentCode.EnrollmentCodeId ||
        null,
      deviceType: updateData.deviceType || "Interlock",
      languageID: utilities.getLanguageId(i18n.language),
      convictionCountryId:updateData?.convictionCountryId,
    };
    if (!ids.enrolleeId || !jwt) {
      const registered = await registerEnrollee(contactData);

      cookies.set("lifesaferEnrollment", JSON.stringify(registered), {
        path: "/",
        sameSite: "strict",
      });

      if (!registered?.jwtToken) {
        persistSession("Contact", contactData, registered || {});
        if (["TEXT", "EMAIL"].includes(registered?.resetSent)) {
          navigate("/verify", {
            state: { enrollee: registered, lifeSaferUser: contactData },
          });
          return false;
        }
        if (registered?.resetSent === "CALL") {
          navigate("/contact-page");
          return false;
        }
      }

      ids = {
        enrolleeId: registered?.enrolleeId || registered?.EnrolleeId,
        userId: registered?.userId || registered?.UserId,
        clientId: registered?.clientId || registered?.ClientId,
      };

      if (!ids.enrolleeId || !ids.userId) {
        throw new Error(
          "Registration did not return valid enrollee and user IDs.",
        );
      }

      await enrolleeUpdate({
        ...contactData,
        enrolleeId: ids.enrolleeId,
      });

      if (!ids.clientId) {
        const savedClient = await saveEnrollee(
          buildClientSavePayload(contactData, ids),
        );
        ids = {
          enrolleeId:
            savedClient?.enrolleeId ||
            savedClient?.EnrolleeId ||
            ids.enrolleeId,
          userId: savedClient?.userId || savedClient?.UserId || ids.userId,
          clientId: savedClient?.clientId || savedClient?.ClientId,
        };
      }

      if (!ids.clientId) {
        throw new Error("Client save did not return a valid client ID.");
      }

      await clientUpdate(createClientUpdatePayload(contactData, ids), {
        skipEnrolleeUpdate: true,
      });
    } else {
      await enrolleeUpdate({
        ...contactData,
        enrolleeId: ids.enrolleeId,
      });
      if (ids.clientId) {
        await clientUpdate(createClientUpdatePayload(contactData, ids), {
          skipEnrolleeUpdate: true,
        });
      } else {
        const savedClient = await saveEnrollee(
          buildClientSavePayload(contactData, ids),
        );
        ids.clientId = savedClient?.clientId || savedClient?.ClientId;

        if (!ids.clientId) {
          throw new Error("Client save did not return a valid client ID.");
        }

        await clientUpdate(createClientUpdatePayload(contactData, ids), {
          skipEnrolleeUpdate: true,
        });
      }
    }

    persistSession("Contact", contactData, ids);
    return true;
  };

  const resolveClientDeviceType = (clientTypeId) => {
    if (clientTypeId === 5) return "Portable";
    if (clientTypeId === 6) return "Speed";
    if (clientTypeId === 1) return "Interlock";
    return null;
  };

  const handleUpdate = async (currentStep, updateData) => {
    if (updateData) {
      setSubmitting(true);
    }

    enrolleeDataArray.current = {
      ...enrolleeDataArray.current,
      ...updateData,
    };

    const currentEnrolleeUser =
      JSON.parse(sessionStorage.getItem("enrolleeUser")) || enrolleeUser || {};

    const enrolleeID =
      currentEnrolleeUser?.enrolleeId || currentEnrolleeUser?.EnrolleeId;

    const userId = currentEnrolleeUser?.userId || currentEnrolleeUser?.UserId;

    const clientId =
      currentEnrolleeUser?.clientId || currentEnrolleeUser?.ClientId;

    const jwtToken = jwtTokenData?.jwtToken;

    const buildEnrolleeUpdatePayload = ({
      step = currentStep,
      complete = false,
    } = {}) => ({
      enrolleeId: enrolleeID || 0,

      clientId: clientId || 0,

      step,

      idUpload: updateData?.idUpload ?? currentEnrolleeUser?.idUpload ?? false,

      complete,

      enrollmentCodeId:
        updateData?.enrollmentCodeId ??
        currentEnrolleeUser?.enrollmentCodeId ??
        enrollmentCodeData?.enrollmentCodeId ??
        0,

      webUserName:
        updateData?.webUserName ??
        currentEnrolleeUser?.webUserName ??
        "ClientEnrollment",

      page: updateData?.page ?? currentEnrolleeUser?.page ?? Page ?? null,

      referURL:
        updateData?.referURL ??
        currentEnrolleeUser?.referURL ??
        URL?.slice(0, 500) ??
        null,

      story: STORY_NAME,

      contactPreference:
        updateData?.contactPreference ??
        pereferedContact ??
        currentEnrolleeUser?.contactPreference ??
        "Phone",

      deviceType:
        updateData?.deviceType ??
        resolveClientDeviceType(enrolleeUser?.clientTypeId) ??
        "Interlock",

      utM_source: updateData?.utM_source ?? UTM_source ?? null,

      utM_medium: updateData?.utM_medium ?? UTM_medium ?? null,

      utM_campaign: updateData?.utM_campaign ?? UTM_campaign ?? null,

      gA_clientid: updateData?.gA_clientid ?? GA_clientid ?? null,

      gclID: updateData?.gclID ?? gclid ?? null,

      userAgent: updateData?.userAgent ?? UserAgent ?? null,

      ipAddress: updateData?.ipAddress ?? IPAddress ?? null,

      wbraid: updateData?.wbraid ?? wbraid ?? null,

      gbraid: updateData?.gbraid ?? gbraid ?? null,

      fbclid: updateData?.fbclid ?? fbclid ?? null,

      fbp: updateData?.fbp ?? fbp ?? null,

      ttclid: updateData?.ttclid ?? ttclid ?? null,

      rdt_cid: updateData?.rdt_cid ?? rdt_cid ?? null,

      posthog_id:
        updateData?.posthog_id ?? currentEnrolleeUser?.posthog_id ?? null,
    });

    try {
      switch (currentStep) {
        case "Welcome": {
          if (enrolleeID && jwtToken) {
            const payload = buildEnrolleeUpdatePayload({
              step: "Welcome",
            });

            await enrolleeUpdate(payload);
          }

          persistSession("Welcome", updateData || {}, {
            enrolleeId: enrolleeID,
            userId,
            clientId,
          });

          hasSetInitialStep.current = true;
          setActiveStep(1);
          break;
        }

        case "Contact": {
          const saved = await saveContact(updateData);

          if (saved) {
            hasSetInitialStep.current = true;
            setActiveStep(2);
          }

          break;
        }

        case "Vehicle": {
          if (!updateData) {
            const selectedVehicleSession =
              JSON.parse(sessionStorage.getItem("enrolleeUser")) || {};
            const selectedVehicleId = resolveVehicleId({
              ...selectedVehicleSession,
              vehicleId:
                selectedVehicleSession.selectedVehicleID ??
                selectedVehicleSession.vehicleId,
            });

            if (selectedVehicleId) {
              persistVehicleSelection(
                { ...selectedVehicleSession, vehicleId: selectedVehicleId },
                "Vehicle",
              );
            }
          } else {
            const vehicleId = await saveNewVehicle(updateData);

            if (vehicleId) {
              const vehicles = await loadVehicle(clientId, userId);

              const matchedVehicle = vehicles?.find(
                (item) => String(resolveVehicleId(item)) === String(vehicleId),
              );

              persistVehicleSelection(
                {
                  ...updateData,
                  ...(matchedVehicle || {}),
                  vehicleId,
                  clientId,
                  userId,
                  enrolleeId: enrolleeID,
                },
                "Vehicle",
              );
            }
          }

          if (enrolleeID && jwtToken) {
            const payload = buildEnrolleeUpdatePayload({
              step: "Vehicle",
            });

            await enrolleeUpdate(payload);
          }

          setActiveStep(3);
          break;
        }

        case "Address": {
          if (enrolleeID && jwtToken) {
            const payload = buildEnrolleeUpdatePayload({
              step: "Address",
            });

            await enrolleeUpdate(payload);
          }

          if (clientId) {
            await clientUpdate(
              createClientUpdatePayload(updateData, { clientId, userId }),
              {
                skipEnrolleeUpdate: true,
              },
            );
          }

          persistSession("Address", updateData || {}, {
            enrolleeId: enrolleeID,
            userId,
            clientId,
            homeAddrCountry:updateData?.country,
            homeAddressCountryCode:updateData?.countryCode,
          });

          setActiveStep(4);
          break;
        }

        case "Schedule": {
          if (clientId) {
            await clientUpdate(
              createClientUpdatePayload(updateData, { clientId, userId }),
              {
                skipEnrolleeUpdate: true,
              },
            );
          }

          if (enrolleeID && jwtToken) {
            const payload = buildEnrolleeUpdatePayload({
              step: "Schedule",
            });

            await enrolleeUpdate(payload);
          }

          persistSession("Schedule", updateData || {}, {
            enrolleeId: enrolleeID,
            userId,
            clientId,
          });

          break;
        }

        default: {
          console.warn(`Unknown Europe step "${currentStep}"`);
          break;
        }
      }

      hasSetInitialStep.current = true;
    } catch (error) {
      console.error(`Unable to save Europe ${currentStep} step`, error);

      toast.error(`${t("toasts.updateFailed")} ${error?.message || ""}`);
    } finally {
      setIsEnrolleeLoadNeeded(true);
      setSubmitting(false);
    }
  };

  
  useEffect(() => {
    if (!isEnrolleeLoadNeeded || !jwtTokenData?.jwtToken) {
      return;
    }

    const reloadEnrollee = async () => {
      const currentSessionEnrollee =
        JSON.parse(sessionStorage.getItem("enrolleeUser")) || {};

      const currentClientId =
        currentSessionEnrollee?.clientId || currentSessionEnrollee?.ClientId;

      const currentUserId =
        currentSessionEnrollee?.userId || currentSessionEnrollee?.UserId;

      if (!currentClientId || !currentUserId || !jwtTokenData?.jwtToken) {
        setIsEnrolleeLoadNeeded(false);
        return;
      }

      try {
        const result = await loadEnrollee(currentClientId, currentUserId);


        if (result) {
          const returnedProfile =
            result?.clientProfile ||
            result?.profile ||
            result?.profiles?.[0] ||
            result;

          const selectedVehicleId =
            currentSessionEnrollee.europeVehicleSelectionConfirmed
              ? resolveVehicleId({
                  ...currentSessionEnrollee,
                  vehicleId:
                    currentSessionEnrollee.selectedVehicleID ??
                    currentSessionEnrollee.vehicleId,
                })
              : null;

          const reloadedSession = normalizeEuropeSession(
            {
              ...currentSessionEnrollee,
              ...result,
              homeAddr1:
                returnedProfile?.homeAddr1 ??
                returnedProfile?.HomeAddr1 ??
                returnedProfile?.homeAddress1 ??
                returnedProfile?.HomeAddress1 ??
                returnedProfile?.address1 ??
                currentSessionEnrollee?.homeAddr1 ??
                null,
              homeAddr2:
                returnedProfile?.homeAddr2 ??
                returnedProfile?.HomeAddr2 ??
                returnedProfile?.homeAddress2 ??
                returnedProfile?.HomeAddress2 ??
                returnedProfile?.address2 ??
                currentSessionEnrollee?.homeAddr2 ??
                null,
              city:
                returnedProfile?.city ??
                returnedProfile?.City ??
                currentSessionEnrollee?.city ??
                null,
              state:
                returnedProfile?.state ??
                returnedProfile?.State ??
                returnedProfile?.province ??
                returnedProfile?.Province ??
                currentSessionEnrollee?.state ??
                currentSessionEnrollee?.province ??
                null,
              province:
                returnedProfile?.province ??
                returnedProfile?.Province ??
                returnedProfile?.state ??
                returnedProfile?.State ??
                currentSessionEnrollee?.province ??
                currentSessionEnrollee?.state ??
                null,
              zip:
                returnedProfile?.zip ??
                returnedProfile?.Zip ??
                returnedProfile?.postalCode ??
                returnedProfile?.PostalCode ??
                currentSessionEnrollee?.zip ??
                null,
              country:
                returnedProfile?.country ??
                returnedProfile?.Country ??
                returnedProfile?.countryCode ??
                returnedProfile?.CountryCode ??
                currentSessionEnrollee?.country ??
                null,
              countryCode:
                returnedProfile?.countryCode ??
                returnedProfile?.CountryCode ??
                returnedProfile?.country ??
                returnedProfile?.Country ??
                currentSessionEnrollee?.countryCode ??
                currentSessionEnrollee?.country ??
                null,
              ...(selectedVehicleId
                ? {
                    vehicleId: selectedVehicleId,
                    VehicleId: selectedVehicleId,
                    VehicleID: selectedVehicleId,
                    selectedVehicleID: selectedVehicleId,
                    europeVehicleSelectionConfirmed: true,
                  }
                : {}),
              HearAboutID:
                result?.HearAboutID ??
                currentSessionEnrollee?.HearAboutID ??
                null,
              HearAboutName:
                result?.HearAboutName ??
                currentSessionEnrollee?.HearAboutName ??
                null,
              deviceType:
                result?.clientTypeId === 5
                  ? "Portable"
                  : result?.clientTypeId === 6
                    ? "Speed"
                    : "Interlock",
            },
            currentSessionEnrollee?.step ??
              currentSessionEnrollee?.Step ??
              result?.step ??
              result?.Step,
          );
          sessionStorage.setItem(
            "enrolleeUser",
            JSON.stringify(reloadedSession),
          );

          if (result?.contactPreference) {
            setPreferedContact(result.contactPreference);
          }
        }
      } catch (error) {
        console.error("Europe - loadEnrollee failed:", error);
      } finally {
        setIsEnrolleeLoadNeeded(false);
      }
    };

    reloadEnrollee();
  }, [isEnrolleeLoadNeeded, jwtTokenData?.jwtToken, loadEnrollee]);

  
  useEffect(() => {
    if (vehicleResumeHandledRef.current || activeStep <= VEHICLE_STEP_INDEX) {
      return;
    }

    const currentSession =
      JSON.parse(sessionStorage.getItem("enrolleeUser")) || {};
    if (currentSession.europeVehicleSelectionConfirmed) {
      vehicleResumeHandledRef.current = true;
      return;
    }

    const currentStep = String(currentSession.step || "").toLowerCase();
    const resumedAfterVehicle = ["address", "schedule"].includes(currentStep);

    if (!resumedAfterVehicle) return;

    const ids = resolveIds();
    if (!ids.clientId || !ids.userId) {
      vehicleResumeHandledRef.current = true;
      return;
    }

    let isActive = true;
    loadVehicle(ids.clientId, ids.userId)
      .then((vehicleData) => {
        if (!isActive) return;

        const vehicles = Array.isArray(vehicleData)
          ? vehicleData.filter((vehicle) => resolveVehicleId(vehicle))
          : [];

        if (vehicles.length > 1) {
          setActiveStep(VEHICLE_STEP_INDEX);
        } else if (vehicles.length === 1) {
          persistVehicleSelection(
            vehicles[0],
            currentSession.step || currentStep,
          );
        }

        vehicleResumeHandledRef.current = true;
      })
      .catch(() => {
        vehicleResumeHandledRef.current = true;
      });

    return () => {
      isActive = false;
    };
  }, [activeStep, loadVehicle, setActiveStep]);

  const goToBackStep = () => setActiveStep((step) => Math.max(0, step - 1));

  const stepsInfo = [
    { title: null, description: null },
    {
      title: t("europe.aboutYouTitle"),
      description: t("europe.aboutYouDescription"),
    },
    { title: t("steps.vehicleInfo"), description: null },
    { title: t("europe.addressTitle"), description: null },
    {
      title: t("europe.scheduleTitle"),
      description: t("europe.scheduleDescription"),
    },
  ];

  const Progress = () => (
    <Grid
      sx={{
        display: "flex",
        alignItems: "center",
        width: { xs: "90%", md: "55%" },
        mx: "auto",
        mt: 1,
      }}
    >
      {[1, 2, 3, 4].map((number) => (
        <React.Fragment key={number}>
          <Grid
            sx={{
              width: { xs: 44, md: 36 },
              height: { xs: 44, md: 36 },
              borderRadius: "50%",
              outline: `2px solid ${number <= activeStep ? "#65b83c" : "#d7d7d7"}`,
              border: `3px solid ${number <= activeStep ? "#FFFFFf" : "#FFFFFf"}`,
              color: number <= activeStep ? "#1c355e" : "#777",
              bgcolor: number <= activeStep ? "#76bc21" : "#d7d7d7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              flexShrink: 0,
              zIndex: 1,
            }}
          >
            {number}
          </Grid>
          {number < 4 && (
            <Grid
              sx={{
                height: 5,
                flex: 1,
                background:
                  number < activeStep
                    ? "#65b83c"
                    : number === activeStep
                      ? "linear-gradient(to right, #65b83c 50%, #ddd 50%)"
                      : "#ddd",
              }}
            />
          )}
        </React.Fragment>
      ))}
    </Grid>
  );

  const Welcome = () => (
    <Grid
      sx={{
        minHeight: { xs: "calc(100vh - 150px)", md: "calc(100vh - 48px)" },
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 2,
        backgroundImage: {
          xs: `url(${BackgroundMobile})`,
          md: `url(${Background})`,
        },
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Grid sx={{ mt: { xs: 28, md: 16 } }}>
        <Typography variant="h4" sx={{ color: "#4b9cd3", fontWeight: 700 }}>
          {t("europe.welcomeTitle")}
        </Typography>
        <Typography sx={{ mt: 1, fontSize: { xs: 16 } }}>
          {t("europe.welcomeDescription")}
        </Typography>
        <Grid sx={{ mt: 1, textAlign: "center" }}>
          {[
            ["GDPR-compliant", "GDPR-compliant"],
            ["Securebydesign", "Secure by design"],
            ["Discreetandeasytouse", "Discreet and easy to use"],
          ].map(([key, fallback]) => (
            <Typography key={key} sx={{ fontSize: 16, my: 0.5 }}>
              <TaskAltIcon
                sx={{ color: "#76bc21", mr: 1, verticalAlign: "middle" }}
              />
              {t(`europe.${key}`, fallback)}
            </Typography>
          ))}
        </Grid>
        <Typography sx={{ mt: 2, color: "#707070", fontSize: 16 }}>
          {t("europe.duration")}
        </Typography>
        <Grid sx={{ mt: 3 }}>
          <ContinueButton
            fullWidth
            variant="contained"
            disabled={submitting}
            showForwardIcon={false}
            continueText="europe.getStarted"
            isLongWidth
            onClick={() => handleUpdate("Welcome", { Step: "Welcome" })}
            sx={{ mt: 3, color: "#76bc21", maxWidth: 300, width: "100%" }}
            isFullWidth={true}
          >
            {/* {t("europe.getStarted")} */}
          </ContinueButton>
        </Grid>
      </Grid>
    </Grid>
  );

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return <Welcome />;
      case 1:
        return (
          <ContactInfo
            setSubmitting={setSubmitting}
            submitting={submitting}
            goToBackStep={null}
            contactInfoForm={contactInfoForm}
            pereferedContact={pereferedContact}
            setPreferedContact={setPreferedContact}
            handleUpdate={handleUpdate}
            isLoaded={isLoaded}
            activeStep={activeStep}
            europeMode
            enrolleeData={enrolleeData}
          />
        );
      case 2:
        return (
          <VehicleInfo
            submitting={submitting}
            goToBackStep={goToBackStep}
            handleUpdate={handleUpdate}
          />
        );
      case 3:
        return (
          <EuropeAddressInfo
            submitting={submitting}
            goToBackStep={goToBackStep}
            handleUpdate={handleUpdate}
          />
        );
      case 4:
        return (
          <EuropeScheduleInfo
            submitting={submitting}
            handleUpdate={handleUpdate}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {activeStep > 0 && <Progress />}
      {activeStep > 0 && stepsInfo[activeStep] && (
        <Grid sx={{ mt: 2, px: 2, textAlign: "center" }}>
          <Typography variant="h5" sx={{ color: "#4b9cd3", fontWeight: 600 }}>
            {stepsInfo[activeStep].title}
          </Typography>
          {stepsInfo[activeStep].description && (
            <Typography sx={{ mt: 1 }}>
              {stepsInfo[activeStep].description}
            </Typography>
          )}
        </Grid>
      )}
      <Grid sx={{ display: "flex", justifyContent: "center" }}>
        {renderStep()}
      </Grid>
    </>
  );
};

const mapDispatchToProps = {
  registerEnrollee,
  enrolleeUpdate,
  saveEnrollee,
  clientUpdate,
  saveNewVehicle,
  loadVehicle,
  loadEnrollee,
};

EuropeStory.propTypes = {
  activeStep: PropTypes.number.isRequired,
  setActiveStep: PropTypes.func.isRequired,
  hasSetInitialStep: PropTypes.object.isRequired,
  isLoaded: PropTypes.bool,
  registerEnrollee: PropTypes.func.isRequired,
  enrolleeUpdate: PropTypes.func.isRequired,
  saveEnrollee: PropTypes.func.isRequired,
  clientUpdate: PropTypes.func.isRequired,
  saveNewVehicle: PropTypes.func.isRequired,
  loadVehicle: PropTypes.func.isRequired,
  loadEnrollee: PropTypes.func.isRequired,
};

export default connect(null, mapDispatchToProps)(EuropeStory);
